import { API_URLS, createApiHeaders } from '../config/api';
import { AUTH_TOKEN_SKEW_SECONDS } from '../config/constants';
import config from '../config';

// Google OAuth Client ID - Updated for new client
export const GOOGLE_CLIENT_ID_VALUE =
  '565474576233-nosbg7cot8t8hp6ejfhovgniun7bjh7b.apps.googleusercontent.com';

class AuthService {
  constructor() {
    // Use config.storage keys to ensure consistency with AuthCallback
    // This prevents mismatches if environment variables are set
    this.tokenKey = config.storage.AUTH_TOKEN;
    this.userKey = config.storage.USER_DATA;
    this.tenantKey = config.storage.TENANT_DATA;
  }

  // ============================================================================
  // SSO Authentication Methods
  // All authentication now goes through the centralized SSO flow
  // Deprecated methods removed - use SSO endpoints instead
  // ============================================================================

  // Verify session with backend
  async verifySession() {
    try {
      const token = this.getToken();
      if (!token) {
        return { isValid: false, user: null };
      }

      const response = await fetch(API_URLS.AUTH_VERIFY, {
        method: 'POST',
        headers: {
          ...createApiHeaders(),
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          token: token,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        return {
          isValid: data.valid || true, // Handle both old and new response formats
          user: data.user
            ? {
                ...data.user,
                user_id: data.user.user_id || data.user.email,
              }
            : null,
          tenant: data.tenant
            ? {
                tenant_id: data.tenant.id || data.tenant.tenant_id,
                alias: data.tenant.alias,
                tenant_name:
                  data.tenant.name ||
                  data.tenant.tenant_name ||
                  data.tenant.alias,
                display_name: data.tenant.display_name || data.tenant.alias,
              }
            : null,
        };
      } else if (response.status === 401) {
        // Token expired or invalid - preserve tenant info for redirect
        const tenant = this.getTenant();
        // Clear token and user but keep tenant temporarily
        this.clearToken();
        this.clearUser();
        // Don't clear tenant - let ProtectedRoute handle redirect with tenant
        return {
          isValid: false,
          user: null,
          tenant: tenant,
          error: 'auth_error',
        };
      } else {
        // Other error - clear local storage
        this.logout();
        return { isValid: false, user: null };
      }
    } catch (error) {
      // On error, don't clear authentication - might be network issue
      // Return error info so caller can decide what to do
      const tenant = this.getTenant();
      // Don't clear token/user on network errors - keep user logged in
      // Only return invalid if it's a clear auth error
      const isAuthError =
        error.message &&
        (error.message.includes('401') ||
          error.message.includes('Unauthorized') ||
          error.message.includes('Invalid token'));
      if (isAuthError) {
        // Only clear on explicit auth errors
        this.clearToken();
        this.clearUser();
        return {
          isValid: false,
          user: null,
          tenant: tenant,
          error: 'auth_error',
        };
      }
      // For network/timeout errors, return valid but with error flag
      // This allows the caller to keep the user logged in
      return {
        isValid: true,
        user: null,
        tenant: tenant,
        error: 'network_error',
      };
    }
  }

  // ================================
  // Token helpers
  // ================================
  _decodeJwt(token) {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch (_) {
      return null;
    }
  }

  isTokenValid(skewSeconds = AUTH_TOKEN_SKEW_SECONDS) {
    const token = this.getToken();
    if (!token) return false;

    try {
      const payload = this._decodeJwt(token);
      if (!payload || !payload.exp) {
        // Token exists but can't be decoded - let backend verify
        // This handles edge cases where token format might be different
        return true; // Return true to allow backend verification
      }

      const nowSec = Math.floor(Date.now() / 1000);
      // Use negative skew for client-side check - be more lenient
      // If token is expired by less than skew seconds, still consider it valid
      // The backend will make the final decision
      return payload.exp > nowSec - skewSeconds;
    } catch (error) {
      // If decoding fails, let backend verify - don't immediately invalidate
      return true; // Return true to allow backend verification
    }
  }

  getTokenExpiry() {
    const token = this.getToken();
    const payload = token ? this._decodeJwt(token) : null;
    return payload && payload.exp ? payload.exp * 1000 : null;
  }

  // Logout
  async logout() {
    // Preserve tenant alias before clearing (for redirect to login page)
    const tenant = this.getTenant();
    const tenantAlias = tenant
      ? typeof tenant === 'string'
        ? tenant
        : tenant.alias || tenant.tenant_id
      : null;

    try {
      const token = this.getToken();
      if (token) {
        // Call backend logout endpoint
        await fetch(API_URLS.AUTH_LOGOUT, {
          method: 'POST',
          headers: {
            ...createApiHeaders(),
            Authorization: `Bearer ${token}`,
          },
        });
      }
    } catch (error) {
      // Handle logout error silently
    } finally {
      // Clear local storage
      this.clearToken();
      this.clearUser();
      this.clearTenant();

      // Also clear any matching keys from sessionStorage
      try {
        sessionStorage.removeItem(this.tokenKey);
        sessionStorage.removeItem(this.userKey);
        sessionStorage.removeItem(this.tenantKey);
      } catch (_) {
        // Ignore sessionStorage errors (e.g., unavailable in some environments)
      }

      // Clear Google OAuth data to allow account switching
      if (window.google && window.google.accounts) {
        try {
          // Simple logout without complex FedCM operations
        } catch (error) {
          // Handle cleanup error silently
        }
      }

      // Trigger logout event with preserved tenant alias
      window.dispatchEvent(
        new CustomEvent('auth:logout', {
          detail: { tenantAlias },
        })
      );
    }
  }

  // Token management
  setToken(token) {
    localStorage.setItem(this.tokenKey, token);
  }

  getToken() {
    return localStorage.getItem(this.tokenKey);
  }

  clearToken() {
    localStorage.removeItem(this.tokenKey);
  }

  // User management
  setUser(user) {
    localStorage.setItem(this.userKey, JSON.stringify(user));
  }

  getUser() {
    const user = localStorage.getItem(this.userKey);
    return user ? JSON.parse(user) : null;
  }

  clearUser() {
    localStorage.removeItem(this.userKey);
  }

  // Tenant management
  setTenant(tenant) {
    localStorage.setItem(this.tenantKey, JSON.stringify(tenant));
  }

  getTenant() {
    const tenant = localStorage.getItem(this.tenantKey);
    return tenant ? JSON.parse(tenant) : null;
  }

  clearTenant() {
    localStorage.removeItem(this.tenantKey);
  }

  // Check if user is authenticated
  isAuthenticated() {
    return !!this.getToken() && !!this.getUser();
  }

  // Get authenticated headers for API calls
  getAuthHeaders() {
    const token = this.getToken();
    const headers = createApiHeaders();

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return headers;
  }

  // Clear Google OAuth state for account switching
  clearGoogleOAuthState() {
    if (window.google && window.google.accounts) {
      try {
        // Simple state clearing without FedCM operations
      } catch (error) {
        // Handle state clearing error silently
      }
    }
  }
}

// Create singleton instance
const authService = new AuthService();

export default authService;
